import styled from "@emotion/styled";
import { AddRounded, EditRounded, DeleteRounded } from "@mui/icons-material";
import {
	Box,
	Button,
	Dialog,
	Divider,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useEffect, useState } from "react";

import RenderInputField from "components/ReusableComponents/RenderInputField/RenderInputField";
import { getColDefaultValue } from "components/PimDashboard/data";
import { GlobalState } from "context/GlobalContext";
import { arrType, inputTypes, selectOptionsKeyName } from "data/globalData";
import axios from "axios";

import { departments } from "data/authData";
import {
	getTrimMasterPayloadWithItemDescription,
	trimCategoryColumn,
	trimCategoryDropdowns,
	trimCategoryDropdownsObj,
	trimSubCategoryDropdowns,
	trimsMasterColumnsObj,
	trimFileTypes,
} from "../data";
import { TrimCommonInputs, TrimInterliningExtraInputs, TrimMainInputs, TrimZipperExtraInputs } from "./data";
import SupplierRowModal from "./SupplierRowModal/SupplierRowModal";

const createRowKey = () => `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const stripClientFields = (row = {}) => {
	const { __rowKey, __isNew, __isDeleted, createdAt, updatedAt, ...rest } = row;
	return rest;
};

const CreateModal = ({ open, setOpen, handleSaveFunction = () => {}, selectedRow, setSelectedRow, blockSupplierDetails = false }) => {
	const { snackBar, setSnackBarOpen, setSnackBar, setLoadingOpen } = GlobalState();
	const VisuallyHiddenInput = styled("input")({
		clip: "rect(0 0 0 0)",
		clipPath: "inset(50%)",
		height: 1,
		overflow: "hidden",
		position: "absolute",
		bottom: 0,
		left: 0,
		whiteSpace: "nowrap",
		width: 1,
	});

	const defPageData = [trimCategoryColumn, ...Object.values(trimsMasterColumnsObj)]
		.map((col) => ({ [col.key]: getColDefaultValue(col) }))
		.reduce((acc, row) => ({ ...acc, ...row }), {});

	const [pageData, setPageData] = useState(defPageData);
	const [errorObj, setErrorObj] = useState({});
	const [supplierRows, setSupplierRows] = useState([]);
	const [supplierModalOpen, setSupplierModalOpen] = useState(false);
	const [supplierModalMode, setSupplierModalMode] = useState("create");
	const [selectedSupplierRow, setSelectedSupplierRow] = useState(null);
	const [devFile, setDevFile] = useState(null);

	useEffect(() => {
		if (selectedRow) setPageData({ ...defPageData, ...selectedRow });
	}, [selectedRow]);

	const updatePageData = (nextValueOrUpdater) => {
		setPageData((prev) => {
			const nextRow = typeof nextValueOrUpdater === "function" ? nextValueOrUpdater(prev) : nextValueOrUpdater;
			return getTrimMasterPayloadWithItemDescription(nextRow);
		});
	};

	const handleCloseModal = () => {
		setOpen(false);
		setErrorObj({});
		setPageData(defPageData);
		setSupplierRows([]);
		setDevFile(null);
		setSelectedRow();
		setSelectedSupplierRow(null);
		setSupplierModalOpen(false);
	};

	const uploadTrimImage = async () => {
		if (!pageData?.file) {
			const existingUrl = pageData?.url ?? "";
			return existingUrl.startsWith("blob:") ? "" : existingUrl;
		}

		const formData = new FormData();
		formData.append("file", pageData.file);
		formData.append("fileMeta", JSON.stringify({ department: departments.SOURCING.key, file_type: trimFileTypes.TRIM_IMAGES }));

		const uploadToBucket = await axios.post("/api/upload/single_upload_to_public_bucket", formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});

		const result = uploadToBucket?.data?.result;
		const bucketUrl = result?.publicUrl ?? result?.url ?? "";
		if (!bucketUrl) {
			throw new Error("Trim image upload failed");
		}

		return bucketUrl;
	};

	const uploadDevelopmentFile = async () => {
		if (!devFile) return null;
		const fileFormData = new FormData();
		fileFormData.append("files", devFile);
		fileFormData.append(
			"fileMeta",
			JSON.stringify({ department: departments.SOURCING.key, file_type: trimFileTypes.TRIM_DEV_FILES }),
		);
		const res = await axios.post("/api/upload/multiple_upload_to_rel_bucket", fileFormData, {
			headers: { "content-type": "multipart/form-data" },
		});
		return res.data.result?.[0] ?? null;
	};
	const handleSupplierSave = (rowPayload) => {
		const nextRow = {
			...rowPayload,
			__rowKey: rowPayload?.__rowKey ?? rowPayload?.trim_supplier_code ?? rowPayload?.id ?? createRowKey(),
		};
		setSupplierRows((prev) => {
			const index = prev.findIndex(
				(row) =>
					row.__rowKey === nextRow.__rowKey ||
					(row.trim_supplier_code && row.trim_supplier_code === nextRow.trim_supplier_code),
			);
			if (index >= 0) {
				const copy = [...prev];
				copy[index] = { ...copy[index], ...nextRow };
				return copy;
			}
			return [...prev, { ...nextRow, __isNew: !nextRow?.trim_supplier_code }];
		});
		setSelectedSupplierRow(null);
		setSupplierModalOpen(false);
	};

	const handleDeleteSupplierRow = (row) => {
		setSupplierRows((prev) => prev.filter((item) => item.__rowKey !== row.__rowKey));
	};

	const handleSaveClick = () => {
		let errObj = {};

		[trimCategoryColumn, ...Object.values(trimsMasterColumnsObj)].forEach((col) => {
			if (col.input_type === inputTypes.AUTO_GENERATE.key) return;
			if (col.constraints?.NOTNULL && !pageData[col.key]) {
				errObj = { ...errObj, [col.key]: `${col.name} is required` };
			}
		});

		if (Object.keys(errObj).length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				severity: "warning",
				message: "Please fill the required fields",
			});
			setErrorObj(errObj);
			return;
		}

		setErrorObj({});
		(async () => {
			setLoadingOpen(true);
			try {
				const uploadedUrl = await uploadTrimImage();
				const developmentFileResult = await uploadDevelopmentFile();
				const payload = getTrimMasterPayloadWithItemDescription({
					...pageData,
					last_updated_by: "",
					created_by: "",
				});
				if (uploadedUrl) payload.url = uploadedUrl;
				else if (payload.url?.startsWith("blob:")) delete payload.url;
				delete payload.file;
				if (developmentFileResult) payload.development_file = developmentFileResult?.url ?? developmentFileResult;
				else delete payload.development_file;

				const supplierRowsToCreate = blockSupplierDetails ? [] : supplierRows.map((row) => stripClientFields(row));
				const savePayload = {
					...payload,
					...(blockSupplierDetails
						? {}
						: {
								styleTrimsSuppliers: supplierRowsToCreate,
								styleTrimsSuppliersToCreate: supplierRowsToCreate,
							}),
				};

				if (typeof handleSaveFunction === "function") await handleSaveFunction(savePayload);
			} catch (error) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					severity: "error",
					message: "Unable to upload trim image",
				});
			} finally {
				setLoadingOpen(false);
			}
		})();
	};

	const openCreateSupplierRow = () => {
		setSelectedSupplierRow(null);
		setSupplierModalMode("create");
		setSupplierModalOpen(true);
	};

	const openEditSupplierRow = (row) => {
		setSelectedSupplierRow(row);
		setSupplierModalMode("update");
		setSupplierModalOpen(true);
	};

	return (
		<Dialog fullWidth maxWidth="md" open={open} onClose={handleCloseModal} scroll="body">
			<Stack width="100%" p={2} boxSizing="border-box" spacing={2}>
				<Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
					<Typography variant="h6">Create Trim Master</Typography>
					<Box display="flex" gap={1} flexWrap="wrap">
						<Button variant="outlined" onClick={handleCloseModal}>
							Cancel
						</Button>
						<Button autoFocus variant="contained" onClick={handleSaveClick}>
							Create
						</Button>
					</Box>
				</Box>

				<Box display="flex" width="100%" flexWrap="wrap" columnGap="3%" rowGap={2}>
					<Box
						component="label"
						width="140px"
						height="160px"
						border={`1px solid ${grey[300]}`}
						borderRadius={1}
						display="flex"
						alignItems="center"
						justifyContent="center"
						overflow="hidden"
						position="relative"
						sx={{ backgroundColor: grey[50], cursor: "pointer" }}>
						{pageData?.url ? (
							<img
								src={pageData.url}
								alt="trim"
								style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute" }}
							/>
						) : (
							<Stack alignItems="center" spacing={0.5}>
								<AddRounded />
								<Typography variant="caption">Add Image</Typography>
							</Stack>
						)}

						<VisuallyHiddenInput
							type="file"
							accept="image/*"
							onChange={(e) => {
								const file = e.target.files?.[0];
								if (!file) return;
								setPageData((prev) => ({ ...prev, file, url: URL.createObjectURL(file) }));
							}}
						/>
					</Box>
					<Box width="calc(100% - 140px - 5%)">
						<TrimMainInputs data={pageData} setData={updatePageData} errorObj={errorObj} mode="create" />
					</Box>
					<TrimCommonInputs data={pageData} setData={updatePageData} errorObj={errorObj} mode="create" />

					<Box width="100%" mt={1}>
						<Divider orientation="horizontal" flexItem />
					</Box>

					{/* Development File */}
					<Box width="100%">
						<Typography variant="body2" fontWeight={600} mb={0.5}>
							Development File
						</Typography>
						<Box display="flex" alignItems="center" gap={1}>
							<Button variant="outlined" size="small" component="label">
								{devFile ? "Change File" : "Upload File"}
								<VisuallyHiddenInput
									type="file"
									onChange={(e) => {
										const f = e.target.files?.[0];
										if (f) setDevFile(f);
									}}
								/>
							</Button>
							{devFile ? (
								<>
									<Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
										{devFile.name}
									</Typography>
									<Button size="small" color="error" onClick={() => setDevFile(null)}>
										Remove
									</Button>
								</>
							) : pageData?.development_file ? (
								<Typography variant="caption" color="text.secondary">
									Existing file uploaded
								</Typography>
							) : (
								<Typography variant="caption" color={grey[400]}>
									No file selected
								</Typography>
							)}
						</Box>
					</Box>

					<Box>
						{pageData?.[trimCategoryColumn.key] === trimCategoryDropdownsObj?.ZIPPER?.key && (
							<TrimZipperExtraInputs data={pageData} setData={updatePageData} errorObj={errorObj} mode="create" />
						)}
						{pageData?.[trimCategoryColumn.key] === trimCategoryDropdownsObj?.INTERLINING?.key && (
							<TrimInterliningExtraInputs data={pageData} setData={updatePageData} errorObj={errorObj} mode="create" />
						)}
					</Box>
				</Box>

				{!blockSupplierDetails && (
				<Box
					sx={{
						bgcolor: "#fff",
						border: `1px solid ${grey[200]}`,
						borderRadius: 2,
						p: 2,
						display: "flex",
						flexDirection: "column",
						minHeight: 0,
					}}>
					<Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} gap={1}>
						<Box>
							<Typography variant="subtitle1" fontWeight={800}>
								Suppliers
							</Typography>
						</Box>
						<Button variant="contained" startIcon={<AddRounded />} onClick={openCreateSupplierRow}>
							Add
						</Button>
					</Box>

					<TableContainer
						component={Paper}
						variant="outlined"
						sx={{
							flex: 1,
							minHeight: 0,
							overflow: "auto",
						}}>
						<Table stickyHeader size="small">
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 700 }}>Supplier</TableCell>
									<TableCell sx={{ fontWeight: 700 }} align="right">
										Price
									</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Currency</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Ref. Code</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
									<TableCell sx={{ fontWeight: 700 }} align="center">
										Actions
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{supplierRows.length ? (
									supplierRows.map((row) => (
										<TableRow key={row.__rowKey} hover>
											<TableCell>{row.supplier || "-"}</TableCell>
											<TableCell align="right">{row.unit_price ?? 0}</TableCell>
											<TableCell>{row.currency || "-"}</TableCell>
											<TableCell>{row.unit || "-"}</TableCell>
											<TableCell>{row.supplier_reference_code || "-"}</TableCell>
											<TableCell>{row.remarks || "-"}</TableCell>
											<TableCell align="center">
												<IconButton size="small" onClick={() => openEditSupplierRow(row)}>
													<EditRounded fontSize="small" />
												</IconButton>
												<IconButton size="small" color="error" onClick={() => handleDeleteSupplierRow(row)}>
													<DeleteRounded fontSize="small" />
												</IconButton>
											</TableCell>
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={7} align="center">
											No supplier rows added yet.
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>
				)}
			</Stack>

			{supplierModalOpen && !blockSupplierDetails && (
				<SupplierRowModal
					key={pageData?.[trimCategoryColumn.key]} // reset modal on category change
					open={supplierModalOpen}
					setOpen={setSupplierModalOpen}
					mode={supplierModalMode}
					selectedRow={selectedSupplierRow}
					selectedTrim={pageData}
					onSave={handleSupplierSave}
				/>
			)}
		</Dialog>
	);
};

export default CreateModal;
